import { memo } from 'react';
import cn from 'classnames';
import type { ChatAttachment } from '../../model/types';

export type MessageQuotePreviewProps = {
  authorName: string;
  text: string | null;
  attachments?: ChatAttachment[];
  isOwn?: boolean;
  onClick?: () => void;
  className?: string;
};

function getQuoteBody(text: string | null, attachments: ChatAttachment[]) {
  const trimmed = text?.trim();
  if (trimmed) {
    return trimmed;
  }

  if (attachments.length) {
    return `Attachment: ${attachments[0].name}`;
  }

  return 'Message';
}

export const MessageQuotePreview = memo(
  ({ authorName, text, attachments = [], isOwn = false, onClick, className }: MessageQuotePreviewProps) => {
    const body = (
      <div
        className={cn(
          'rounded-lg border-l-4 border-primary bg-default-100/80 py-2 pl-3 pr-2 text-left',
          isOwn && 'border-primary/70 bg-white/55',
          className,
        )}
      >
        <p className="text-tiny font-semibold text-default-700">{authorName}</p>
        <p className="mt-0.5 line-clamp-3 text-tiny text-default-600">
          {getQuoteBody(text, attachments)}
        </p>
      </div>
    );

    if (!onClick) {
      return body;
    }

    return (
      <button
        type="button"
        className="mb-2 block w-full transition-colors hover:opacity-90"
        onClick={(event) => {
          event.stopPropagation();
          onClick();
        }}
      >
        {body}
      </button>
    );
  },
);

MessageQuotePreview.displayName = 'MessageQuotePreview';

export function getMessageQuoteAuthorName(sender: {
  firstName: string;
  lastName: string;
  username: string;
} | null): string {
  if (!sender) {
    return 'Unknown';
  }

  return (
    [sender.firstName, sender.lastName].filter(Boolean).join(' ').trim() || sender.username || 'Unknown'
  );
}
